import React from "react";
import makeStyles from "@mui/styles/makeStyles";
import PropTypes from "prop-types";
import {
  List,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box
} from "@mui/material";
import CourseCardIltInfo from "./CourseCardIltInfo";

const fadeHeight = 74;
const expansionPanelWidth = 631;
const expansionPanelListHeight = 289;

const useStyles = makeStyles(theme => ({
  listContainer: {
    maxHeight: expansionPanelListHeight,
    overflowY: "scroll",
    position: "relative"
  },
  list: {
    maxWidth: expansionPanelWidth,
    position: "relative",
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down("lg")]: {
      marginRight: theme.spacing(3)
    },
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(0, 0.25)
    }
  },
  fade: {
    position: "absolute",
    bottom: 24,
    left: 202,
    right: 36,
    background:
      "linear-gradient(to top, rgba(255, 255, 255, 1) 0%,  rgba(255, 255, 255, 0) 100%)",
    [theme.breakpoints.down("md")]: {
      left: 0,
      bottom: 0,
      right: 24
    },
    pointerEvents: "none"
  },
  summary: {
    "&.Mui-expanded": {
      minHeight: 0,
      margin: theme.spacing(0)
    }
  },
  details: {
    marginLeft: 202,
    marginRight: theme.spacing(4),
    marginTop: theme.spacing(0.5),
    [theme.breakpoints.down("md")]: {
      margin: theme.spacing(4, 2, 4, 3)
    },
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(4, 0)
    }
  },
  requirement: {
    padding: theme.spacing(0, 1)
  }
}));

const AssignmentRequirements = ({
  passingPostScore,
  additionalRequirements
}) => {
  let requirements = [];
  if (passingPostScore) {
    requirements.push("Passing Score: " + passingPostScore);
  }
  if (additionalRequirements) {
    requirements.push(additionalRequirements);
  }
  const classes = useStyles();
  return (
    <Typography variant="body2" color="textSecondary">
      <Typography variant="body2" color="textPrimary" component="span">
        {"Requirements:"}
      </Typography>
      {requirements.map((requirement, index) => {
        return (
          <span key={index}>
            <span className={classes.requirement}>{requirement}</span>
            {index !== requirements.length - 1 && <span>{"|"}</span>}
          </span>
        );
      })}
    </Typography>
  );
};

AssignmentRequirements.propTypes = {
  additionalRequirements: PropTypes.arrayOf(PropTypes.string),
  passingPostScore: PropTypes.number
};

export default function CourseCardExpansionPanel(props) {
  const {
    assignmentRequirements,
    items,
    expanded,
    description,
    passingPostScore,
    iltInfo,
    assignmentType
  } = props;
  const classes = useStyles();
  const [currentFadeHeight, setCurrentFadeHeight] = React.useState(0);
  const listRef = React.useRef(null);

  React.useEffect(() => {
    setCurrentFadeHeight(getFadeHeight());
  });

  /**
   * Sets the height of the fade at the bottom of the list. The height will gradually
   * become zero when the user scrolls to the bottom of the list.
   */
  function getFadeHeight() {
    const listHeight = listRef.current.offsetHeight;
    const scrollPosition = listRef.current.offsetParent?.scrollTop;
    return Math.min(
      listHeight - expansionPanelListHeight - scrollPosition,
      fadeHeight
    );
  }
  function handleScroll() {
    setCurrentFadeHeight(getFadeHeight());
  }
  return (
    <Accordion
      expanded={expanded}
      classes={{ root: classes.root, expanded: classes.expanded }}
    >
      <AccordionSummary className={classes.summary} />
      <AccordionDetails classes={{ root: classes.details }}>
        <Box
          maxWidth={expansionPanelWidth}
          zIndex={1}
          display="flex"
          flexDirection="column"
        >
          {!!description && (
            <Box mb={3}>
              <Typography variant="body2" color="textSecondary">
                {description}
              </Typography>
            </Box>
          )}
          {(!!assignmentRequirements || !!passingPostScore) && (
            <Box display="flex" mb={3}>
              <AssignmentRequirements
                passingPostScore={passingPostScore}
                requirements={assignmentRequirements}
              />
            </Box>
          )}
          {!!assignmentType &&
            assignmentType.toLowerCase() === "ilt" &&
            !!iltInfo && (
              <Box display="flex" mb={3}>
                <CourseCardIltInfo
                  location={iltInfo.trainingLocation}
                  instructor={iltInfo.instructor}
                />
              </Box>
            )}
        </Box>
        <div className={classes.listContainer} onScroll={handleScroll}>
          <List
            ref={listRef}
            aria-label="Assignment Activity List"
            classes={{ root: classes.list }}
          >
            {items}
          </List>
        </div>

        {expanded && (
          <div
            className={classes.fade}
            style={{ height: currentFadeHeight + "px" }}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
}

CourseCardExpansionPanel.propTypes = {
  description: PropTypes.string,
  items: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.element
  ]),
  expanded: PropTypes.bool,
  assignmentRequirements: PropTypes.arrayOf(PropTypes.string),
  passingPostScore: PropTypes.number,
  iltInfo: PropTypes.any,
  assignmentType: PropTypes.string
};
